import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UnitItemsTable from "./UnitItemsTable";

const KINDS = [["movable", "Movable Items"], ["immovable", "Immovable Items"]];

async function loadMasters(kind) {
  const [c, s] = await Promise.all([api.get(`/masters/${kind}_item_category`), api.get(`/masters/${kind}_item_subcategory`)]);
  const byCat = {};
  (s.data || []).forEach((x) => { (byCat[x.parent] = byCat[x.parent] || []).push(x.name); });
  return { cats: (c.data || []).map((x) => x.name), byCat };
}

export default function UnitItems() {
  const { unitId } = useParams();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite("immovables") : true;
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({ movable: [], immovable: [] });
  const [masters, setMasters] = useState({ movable: { cats: [], byCat: {} }, immovable: { cats: [], byCat: {} } });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const apply = (d) => {
    setData(d);
    setRows({ movable: d.items.filter((r) => r.kind === "movable"), immovable: d.items.filter((r) => r.kind === "immovable") });
    setDirty(false);
  };

  useEffect(() => {
    api.get(`/units/${unitId}/items`).then(({ data: d }) => apply(d)).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load unit"));
    Promise.all([loadMasters("movable"), loadMasters("immovable")]).then(([m, im]) => setMasters({ movable: m, immovable: im })).catch(() => toast.error("Failed to load masters"));
  }, [unitId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = async () => {
    const all = [...rows.movable, ...rows.immovable];
    const bad = all.findIndex((r) => !r.category || !r.item || !(Number(r.quantity) >= 1));
    if (bad >= 0) return toast.error(`${all[bad].kind === "movable" ? "Movable" : "Immovable"} row ${(all[bad].kind === "movable" ? bad : bad - rows.movable.length) + 1}: category, item and quantity (≥1) are required`);
    setSaving(true);
    try {
      const items = all.map((r) => ({ ...r, quantity: parseInt(r.quantity, 10), value: Number(r.value) || 0 }));
      const { data: d } = await api.put(`/units/${unitId}/items`, { items });
      apply(d);
      toast.success("Items saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-8 text-sm text-[#6b655b]" data-testid="ui-loading">Loading…</div>;
  const u = data.unit;
  return (
    <div className="p-8 md:p-12 max-w-[1400px] mx-auto space-y-6" data-testid="unit-items-page">
      <div>
        <Link to="/admin/properties?tab=units" className="inline-flex items-center gap-1 text-xs text-[#234333] hover:underline" data-testid="ui-back"><ArrowLeft size={13} /> Back to Units</Link>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b] mt-4">Trust Estate · Unit Items</div>
        <h1 className="mt-1 font-serif text-3xl text-[#1c1a17]" data-testid="ui-unit-name">{u.name}</h1>
        <div className="text-sm text-[#6b655b] mt-1">{[u.property_name, u.floor && `${u.floor} floor`, u.assigned_room_no && `Room ${u.assigned_room_no}`, u.unit_type].filter(Boolean).join(" · ")}</div>
      </div>
      {KINDS.map(([k, title]) => (
        <UnitItemsTable key={k} kind={k} title={title} rows={rows[k]} cats={masters[k].cats} itemsByCat={masters[k].byCat} writable={writable}
          onRowsChange={(r) => { setRows((p) => ({ ...p, [k]: r })); setDirty(true); }} />
      ))}
      {writable && (
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-[#e5e0d8] rounded-sm px-5 py-3 flex items-center justify-end gap-3 z-20">
          {dirty ? <span className="text-[11px] text-[#9e2a2b]" data-testid="ui-unsaved">Unsaved changes</span>
            : data.updated_at && <span className="text-[11px] text-[#8a8478]">Last saved {new Date(data.updated_at).toLocaleString()} {data.updated_by && `by ${data.updated_by}`}</span>}
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm font-semibold px-5 py-2.5 rounded-sm disabled:opacity-60" data-testid="ui-save"><Save size={15} />{saving ? "Saving…" : "Save Items"}</button>
        </div>
      )}
    </div>
  );
}
